import {
  OverviewComponentType,
  Filters,
  ProductOverviewRegistry,
} from './types';
import { constants } from 'constants/Constants';

import archletRegistry from './programs/procurement/archlet';
import kazandirioRegistry from './programs/dtc/kazandirio';
import pepsiTastyRewardsRegistry from './programs/dtc/pepsiTastyRewards';
import appRegistry from './programs/rgm/app';
import mesLiteRegistry from './programs/manufacturing/mesLite';
import PepIRiSRegistry from './programs/SE/PepIRiS/PepIRiS';
import gpgRegistry from './programs/gpg';
import tpoRegistry from './programs/dtv/tpo';
import ProfitToServeRegistry from './programs/CTX/ProfitToServe';
import mctRegistry from './programs/manufacturing/mct';
import pricingRegistry from './programs/rgm/pricingAi';
import ipcRegistry from './programs/manufacturing/ipc';

const programs = constants.programs;
const products = constants.products;

/**
 * Central registry that aggregates each product’s registry under its program.
 */
const productRegistries: {
  [program: string]: { [product: string]: ProductOverviewRegistry };
} = {
  [programs.Procurement]: {
    [products.Archlet]: archletRegistry,
  },
  [programs.DTC]: {
    [products.Kazandirio]: kazandirioRegistry,
    [products.PepsiTastyRewards]: pepsiTastyRewardsRegistry,
  },
  [programs.RGM]: {
    [products.APP]: appRegistry,
    [products.PricingAI]: pricingRegistry,
  },
  [programs.Manufacturing]: {
    [products.MesLite]: mesLiteRegistry,
    [products.ManufacturingControlTower]: mctRegistry,
    [products.IPC]: ipcRegistry,
  },
  [programs.SE]: {
    [products.PepIRiS]: PepIRiSRegistry,
  },
  [programs.GPG]: {
    [products.GPG]: gpgRegistry,
  },
  [programs.DTV]: {
    [products.TPO]: tpoRegistry,
  },
  [programs.CTX]: {
    [products.ProfitToServe]: ProfitToServeRegistry,
  },
};

export const OverviewService = {
  /**
   * Registers a new scorecard for a given program and product.
   */
  registerScoreCard(
    program: any,
    product: any,
    component: OverviewComponentType
  ): void {
    const programRegistry = productRegistries[program];
    if (programRegistry && programRegistry[product]) {
      programRegistry[product].scorecards.push(component);
    } else {
      // Optionally, create a new registry if not found:
      if (!productRegistries[program]) {
        productRegistries[program] = {};
      }
      productRegistries[program][product] = {
        scorecards: [component],
        customOverview: null,
      };
    }
  },

  /**
   * Registers (or replaces) a custom overview for a given program and product.
   */
  registerCustomOverview(
    program: number | string,
    product: number | string,
    component: OverviewComponentType
  ): void {
    const programRegistry = productRegistries[program];
    if (programRegistry && programRegistry[product]) {
      programRegistry[product].customOverview = component;
    } else {
      // Optionally, create a new registry if not found:
      if (!productRegistries[program]) {
        productRegistries[program] = {};
      }
      productRegistries[program][product] = {
        scorecards: [],
        customOverview: component,
      };
    }
  },

  /**
   * Returns an array of scorecards for the given filters.
   */
  resolveScoreCards(filters?: Filters): OverviewComponentType[] {
    if (!filters || !filters.program || !filters.product) {
      return [];
    }
    const programRegistry = productRegistries[filters.program];
    if (programRegistry && programRegistry[filters.product]) {
      return programRegistry[filters.product].scorecards;
    }
    return [];
  },

  /**
   * Returns a custom overview component for the given filters, or null if none exists.
   */
  resolveCustomOverview(filters?: Filters): OverviewComponentType | null {
    if (!filters || !filters.program || !filters.product) {
      return null;
    }
    const programRegistry = productRegistries[filters.program];
    if (programRegistry && programRegistry[filters.product]) {
      return programRegistry[filters.product].customOverview || null;
    }
    return null;
  },

  /**
   * (Optional) Clears the registries. Useful for testing or resetting state.
   */
  clearRegistries(): void {
    Object.keys(productRegistries).forEach((program) => {
      Object.keys(productRegistries[program]).forEach((product) => {
        productRegistries[program][product].scorecards = [];
        productRegistries[program][product].customOverview = null;
      });
    });
  },
};
